import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Phone, Mail, HeartHandshake, Clock, ShieldCheck, MessageCircle, Home as HomeIcon, Users, CalendarDays, Play } from "lucide-react";
import CareSlider from "../components/CareSlider";
import PhotoStory from "../components/PhotoStory";
import Newsletter from "../components/Newsletter";
import { useSiteContent } from "../admin/SiteContentContext";

const iconMap = { HeartHandshake, Clock, ShieldCheck, MessageCircle, Home: HomeIcon, Users, CalendarDays };
const iconColorMap = {
  blue: "bg-[#2d7bff]/15 text-[#5aa0ff]",
  lime: "bg-[#c7e34a]/15 text-[#c7e34a]",
  amber: "bg-[#f5b945]/15 text-[#f5b945]",
};

export default function Home() {
  const { content } = useSiteContent();
  const { siteInfo, heroHighlights, serviceCards, testimonials } = content;
  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#2d7bff]/10 blur-3xl pointer-events-none" />
        <div className="absolute -top-20 right-0 w-[420px] h-[420px] rounded-full bg-[#c7e34a]/10 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-14 items-center relative">
          <div>
            <span className="badge-pill"><HeartHandshake className="w-3.5 h-3.5" /> Compassion in action</span>
            <h1 className="hero-heading text-white text-5xl md:text-6xl lg:text-7xl mt-6 mb-6">
              A little more care in <span className="italic text-[#c7e34a]">every day</span>.
            </h1>
            <p className="text-slate-300 text-lg max-w-xl leading-relaxed mb-8">
              Trusted companionship and a helping hand at home — so the people you love can keep living life on their terms.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/booking" className="btn-lime"><Calendar className="w-4 h-4" /> Book Consultation <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/services" className="btn-ghost">Explore Services <ArrowRight className="w-4 h-4" /></Link>
              <a href={`tel:${siteInfo.phoneRaw || siteInfo.phone}`} className="btn-ghost"><Phone className="w-4 h-4" /> {siteInfo.phone}</a>
              {siteInfo.email && (
                <a href={`mailto:${siteInfo.email}`} className="btn-ghost"><Mail className="w-4 h-4" /> {siteInfo.email}</a>
              )}
            </div>

            <div className="mt-10 flex items-center gap-6">
              <div className="flex items-center -space-x-3">
                <div className="w-9 h-9 rounded-full border-2 border-[#0a1020] overflow-hidden"><img src="https://images.unsplash.com/photo-1543333995-a78aea2eee50?w=120&q=70" alt="" className="w-full h-full object-cover" /></div>
                <div className="w-9 h-9 rounded-full border-2 border-[#0a1020] overflow-hidden"><img src="https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?w=120&q=70" alt="" className="w-full h-full object-cover" /></div>
                <div className="w-9 h-9 rounded-full border-2 border-[#0a1020] overflow-hidden"><img src="https://images.unsplash.com/photo-1540778339538-067eae485e9f?w=120&q=70" alt="" className="w-full h-full object-cover" /></div>
              </div>
              <div className="text-sm">
                <div className="text-white font-medium">Reliable & respectful</div>
                <div className="text-slate-400">Person-first support</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-[28px] overflow-hidden border border-white/10 shadow-2xl">
              <div className="aspect-[4/5] relative">
                <img src="https://images.unsplash.com/photo-1543333995-a78aea2eee50?w=1200&q=80" alt="Companionship at home" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070b17] via-transparent to-transparent" />
                <div className="absolute left-5 top-5">
                  <span className="badge-pill"><HeartHandshake className="w-3.5 h-3.5" /> Trusted companionship</span>
                </div>
                <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#c7e34a] font-semibold mb-1">Compassion in action</div>
                    <div className="text-white font-serif-display text-2xl">Care that feels like family</div>
                  </div>
                  <a href={`tel:${siteInfo.phoneRaw}`} className="shrink-0 bg-[#c7e34a] text-[#0b1220] rounded-full px-4 py-2 text-sm font-semibold hover:brightness-105 transition flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5" /> Call us
                  </a>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#0b1324] border border-white/10 rounded-2xl p-4 shadow-xl hidden md:block">
              <p className="text-xs text-slate-400 max-w-[200px] leading-relaxed">Compassionate, dependable non-medical support for everyday life at home.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="border-y border-white/5 bg-[#080e1c]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid md:grid-cols-3 gap-8">
          {heroHighlights.map(h => {
            const Icon = iconMap[h.icon];
            return (
              <div key={h.title} className="flex items-start gap-4">
                <div className={`icon-chip ${iconColorMap[h.color]}`}><Icon className="w-5 h-5" /></div>
                <div>
                  <h4 className="text-white font-semibold text-lg">{h.title}</h4>
                  <p className="text-slate-400 text-sm mt-1">{h.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Meet Dazile */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="rounded-[28px] overflow-hidden border border-white/10">
              <img src="https://images.unsplash.com/photo-1762955913084-96ea3f986468?w=1200&q=80" alt="Leadership with heart" className="w-full aspect-square object-cover" />
            </div>
            <div className="absolute left-5 bottom-5 badge-pill">Leadership with heart</div>
          </div>

          <div>
            <span className="kicker">Leadership with heart</span>
            <h2 className="font-serif-display text-white text-4xl md:text-5xl lg:text-6xl mt-4 mb-6">Meet Dazile Auguste.</h2>
            <p className="text-slate-300 leading-relaxed mb-5">
              3E DanCare was shaped around a belief that dependable help should still feel deeply human. Dazile leads with compassion, clear communication, and respect for every client's choices, routines, and dignity.
            </p>
            <div className="card-soft p-6 my-6 border-l-2 border-l-[#c7e34a]">
              <p className="font-serif-display text-white text-xl leading-snug">“{siteInfo.ceoQuote}”</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/booking" className="btn-lime"><Calendar className="w-4 h-4" /> Book a consultation</Link>
              <a href={`tel:${siteInfo.phoneRaw || siteInfo.phone}`} className="btn-ghost"><Phone className="w-4 h-4" /> {siteInfo.phone}</a>
              {siteInfo.email && (
                <a href={`mailto:${siteInfo.email}`} className="btn-ghost"><Mail className="w-4 h-4" /> {siteInfo.email}</a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-24 bg-[#080e1c] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <span className="kicker">Made for real life</span>
            <h2 className="font-serif-display text-white text-4xl md:text-5xl lg:text-6xl mt-4 mb-6">Small moments are a big part of care.</h2>
            <p className="text-slate-300 max-w-md leading-relaxed mb-8">
              Whether it is a morning walk, a favorite meal, or a little help keeping the house in order, our companions and homemakers help make the day feel more manageable.
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 text-[#c7e34a] hover:gap-3 transition-all font-semibold">
              Meet 3E DanCare <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {serviceCards.map(s => {
              const Icon = iconMap[s.icon];
              return (
                <div key={s.title} className="card-soft p-7 hover:border-[#c7e34a]/30 transition-colors group">
                  <div className="icon-chip bg-[#c7e34a]/10 text-[#c7e34a] mb-5 group-hover:bg-[#c7e34a]/20 transition"><Icon className="w-5 h-5" /></div>
                  <h3 className="font-serif-display text-white text-2xl mb-2">{s.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Short care story */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="kicker"><Play className="w-3.5 h-3.5" /> A short care story</span>
            <h2 className="font-serif-display text-white text-4xl md:text-5xl lg:text-6xl mt-4 mb-6">A little care can change the shape of a day.</h2>
            <p className="text-slate-300 max-w-md leading-relaxed mb-8">Watch a short visual story, then explore a care message slider built to grow to 1,000 health-and-care slides.</p>
            <div className="card-soft p-5 flex items-center gap-3 max-w-md">
              <HeartHandshake className="w-5 h-5 text-[#c7e34a]" />
              <span className="text-sm text-slate-200">Thoughtful, non-medical support for everyday living.</span>
            </div>
            <a href={`tel:${siteInfo.phoneRaw || siteInfo.phone}`} className="mt-6 inline-flex items-center gap-2 text-[#5aa0ff] hover:text-[#8ab8ff]">
              <Phone className="w-4 h-4" /> Dazile Auguste · {siteInfo.phone}
            </a>
            {siteInfo.email && (
              <a href={`mailto:${siteInfo.email}`} className="mt-2 inline-flex items-center gap-2 text-[#c7e34a] hover:text-[#d4ec6a]">
                <Mail className="w-4 h-4" /> {siteInfo.email}
              </a>
            )}
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 group cursor-pointer">
            <img src="https://images.unsplash.com/photo-1543333995-a78aea2eee50?w=1400&q=80" alt="care story" className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070b17] via-transparent to-transparent" />
            <button className="absolute inset-0 flex items-center justify-center" aria-label="play">
              <span className="w-20 h-20 rounded-full bg-[#c7e34a] text-[#0b1220] flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
                <Play className="w-8 h-8 fill-[#0b1220]" />
              </span>
            </button>
            <div className="absolute left-6 bottom-6 right-6 flex items-end justify-between text-sm">
              <span className="text-slate-200">Companionship and homemaker support, in motion.</span>
              <span className="text-[#c7e34a]">{siteInfo.ceo}</span>
            </div>
          </div>
        </div>
      </section>

      <CareSlider />
      <PhotoStory />
      <Newsletter />

      {/* Clarity note */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="card-soft p-10 md:p-14 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#2d7bff]/10 blur-3xl" />
            <div className="relative">
              <span className="kicker"><ShieldCheck className="w-3.5 h-3.5" /> A note of clarity</span>
              <h3 className="font-serif-display text-white text-4xl md:text-5xl mt-4 mb-5">Here for everyday support — not medical care.</h3>
              <p className="text-slate-300 leading-relaxed max-w-3xl mb-8">
                3E DanCare LLC provides non-medical companion and homemaker services. We do not provide skilled nursing or medical services. Our role is to make daily life feel safer, lighter, and more connected.
              </p>
              <Link to="/services" className="btn-ghost">Understand our services <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#080e1c] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-14">
            <span className="kicker">Words from our circle</span>
            <h2 className="font-serif-display text-white text-4xl md:text-5xl mt-4">Care is felt.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map(t => (
              <div key={t.name} className="card-soft p-7">
                <div className="quote-mark">“</div>
                <p className="text-slate-200 leading-relaxed -mt-3 mb-6">{t.text}</p>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-[#c7e34a]/15 border border-[#c7e34a]/30 text-[#c7e34a] flex items-center justify-center font-semibold">{t.initial}</div>
                  <div>
                    <div className="text-white text-sm font-medium">{t.name}</div>
                    <div className="text-slate-500 text-xs">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-400 mt-14">You deserve support that feels like a partnership.</p>
          <div className="text-center mt-5">
            <Link to="/booking" className="btn-lime"><Calendar className="w-4 h-4" /> Start a consultation</Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-serif-display text-white text-4xl md:text-6xl mb-6">Let's make the next day a little easier.</h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-9">Tell us what support would look like for your family. There is no pressure and no perfect way to begin.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/booking" className="btn-lime"><Calendar className="w-4 h-4" /> Book Consultation</Link>
            <a href={`tel:${siteInfo.tollFree}`} className="btn-ghost"><Phone className="w-4 h-4" /> Call {siteInfo.tollFree}</a>
            {siteInfo.email && (
              <a href={`mailto:${siteInfo.email}`} className="btn-ghost"><Mail className="w-4 h-4" /> Email us</a>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
